import '@/styles/globals.css'
import 'nprogress/nprogress.css'
import NProgress from 'nprogress'
import AnalyticsProvider, {
  initializeAnalytics,
} from '@/components/AnalyticsProvider'
initializeAnalytics()
import ErrorTrackingProvider from '@/components/ErrorTrackingProvider'
import { Analytics } from '@vercel/analytics/react'
import { Inter } from '@next/font/google'
import type { AppContext, AppProps } from 'next/app'
import { default as NextApp } from 'next/app'
import { ThemeProvider, useTheme } from 'next-themes'
import { darkTheme, globalReset } from '@/stitches.config'
import '@rainbow-me/rainbowkit/styles.css'
import {
  connectorsForWallets,
  RainbowKitProvider,
  getDefaultWallets,
  darkTheme as rainbowDarkTheme,
  lightTheme as rainbowLightTheme,
} from '@rainbow-me/rainbowkit'
import {
  argentWallet,
  trustWallet,
  ledgerWallet,
  injectedWallet
} from '@rainbow-me/rainbowkit/wallets';
import { WagmiConfig, createConfig, configureChains } from 'wagmi'
import * as Tooltip from '@radix-ui/react-tooltip'
import { publicProvider } from 'wagmi/providers/public' 
import Script from "next/script";

import { createWeb3Modal } from '@web3modal/wagmi/react'

import {
  ReservoirKitProvider,
  darkTheme as reservoirDarkTheme,
  lightTheme as reservoirLightTheme,
  ReservoirKitTheme,
  CartProvider,
} from '@reservoir0x/reservoir-kit-ui'
import { FC, useEffect, useContext, useState } from 'react'
import { HotkeysProvider } from 'react-hotkeys-hook'
import ToastContextProvider from '@/context/ToastContextProvider'
import supportedChains from '@/utils/chains'
import { useMarketplaceChain } from '@/hooks'
import ChainContextProvider from '@/context/ChainContextProvider'
import Router from 'next/router'
import { WebsocketContextProvider } from '@/context/WebsocketContextProvider'
import ReferralContextProvider, {
  ReferralContext,
} from '@/context/ReferralContextProvider' 
import { walletConnectProvider } from '@web3modal/wagmi' 


//CONFIGURABLE: Use nextjs to load your own custom font: https://nextjs.org/docs/basic-features/font-optimization
const inter = Inter({
  subsets: ['latin'],
})  

export const NORMALIZE_ROYALTIES = process.env.NEXT_PUBLIC_NORMALIZE_ROYALTIES
  ? process.env.NEXT_PUBLIC_NORMALIZE_ROYALTIES === 'true'
  : false

const projectId = process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID || ""


const { chains, publicClient, webSocketPublicClient } = configureChains(supportedChains, [
  walletConnectProvider({ projectId }),
  publicProvider(),
])

const { wallets } = getDefaultWallets({
  appName: 'CoinPulse Online',
  projectId,
  chains,
})

const connectors = connectorsForWallets([
  ...wallets,
  {
    groupName: 'Recommended',
    wallets: [
      injectedWallet({chains}),
      argentWallet({ projectId, chains }),
      trustWallet({ projectId, chains }),
      ledgerWallet({ projectId, chains })
    ]
  }
]) 


const wagmiConfig  = createConfig({
  autoConnect: true,
  connectors,
  publicClient,
  webSocketPublicClient,
})

createWeb3Modal({ wagmiConfig , projectId, chains })

//CONFIGURABLE: Here you can override any of the theme tokens provided by RK: https://docs.reservoir.tools/docs/reservoir-kit-theming-and-customization
const reservoirKitThemeOverrides = {
  headlineFont: inter.style.fontFamily,
  font: inter.style.fontFamily,
  primaryColor: '#6E56CB',
  primaryHoverColor: '#644fc1',
}
Router.events.on('routeChangeStart', () => NProgress.start())
Router.events.on('routeChangeComplete', () => NProgress.done())
Router.events.on('routeChangeError', () => NProgress.done())

function AppWrapper(props: AppProps & { baseUrl: string }) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      value={{
        dark: darkTheme.className,
        light: 'light',
      }}
    >
      <WagmiConfig config={wagmiConfig }>
        <ChainContextProvider>
          <AnalyticsProvider>
            <ErrorTrackingProvider>
              <ReferralContextProvider>
                <MyApp {...props} />
              </ReferralContextProvider>
            </ErrorTrackingProvider>
          </AnalyticsProvider>
        </ChainContextProvider>
      </WagmiConfig>
    </ThemeProvider>
  )
}

function MyApp({
  Component,
  pageProps,
  baseUrl,
}: AppProps & { baseUrl: string }) {
  globalReset()

  const { theme } = useTheme()
  const marketplaceChain = useMarketplaceChain()
  const [reservoirKitTheme, setReservoirKitTheme] = useState<
    ReservoirKitTheme | undefined
  >()

  const [showChild, setShowChild] = useState(false);
  const [rainbowKitTheme, setRainbowKitTheme] = useState<
    | ReturnType<typeof rainbowDarkTheme>
    | ReturnType<typeof rainbowLightTheme>
    | undefined
  >()

  useEffect(() => {
    setShowChild(true);
  }, []);

  useEffect(() => {
    if (theme == 'dark') {
      setReservoirKitTheme(reservoirDarkTheme(reservoirKitThemeOverrides))
      setRainbowKitTheme(
        rainbowDarkTheme({
          borderRadius: 'small',
        })
      )
    } else {
      setReservoirKitTheme(reservoirLightTheme(reservoirKitThemeOverrides))
      setRainbowKitTheme(
        rainbowLightTheme({
          borderRadius: 'small',
        })
      )
    }
  }, [theme])
  const { feesOnTop } = useContext(ReferralContext)

  const FunctionalComponent = Component as FC

  let source = process.env.NEXT_PUBLIC_MARKETPLACE_SOURCE

  if (!source && process.env.NEXT_PUBLIC_HOST_URL) {
    try {
      const url = new URL(process.env.NEXT_PUBLIC_HOST_URL)
      source = url.host
    } catch (e) { }
  }
  if (!showChild) {
    return null;
  }


  if (typeof window === 'undefined') {
    return
      ; <></>
  }
  else {
    return (
      <HotkeysProvider>
        <Script
          strategy="lazyOnload"
          src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}`}
        />
        <Script strategy="lazyOnload">
          {` 
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}', {
                    page_path: window.location.pathname,
                    });

                    (function(c,l,a,r,i,t,y){
                      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                  })(window, document, "clarity", "script", "id3vif1sru");
                `}
        </Script>
        <ThemeProvider
          attribute="class"
          defaultTheme="dark"
          value={{
            dark: darkTheme.className,
            light: 'light',
          }}
        >
          <ReservoirKitProvider
            options={{
              //CONFIGURABLE: Override any configuration available in RK: https://docs.reservoir.tools/docs/reservoirkit-ui#configuring-reservoirkit-ui
              // Note that you should at the very least configure the source with your own domain
              chains: supportedChains.map(
                ({ reservoirBaseUrl, proxyApi, id, name }) => {
                  return {
                    id,
                    name,
                    baseApiUrl: proxyApi
                      ? `${baseUrl}${proxyApi}`
                      : reservoirBaseUrl,
                    active: marketplaceChain.id === id,
                  }
                }
              ),
              logLevel: 4,
              source: source,
              normalizeRoyalties: NORMALIZE_ROYALTIES,
              disablePoweredByReservoir: true,
              automatedRoyalties: true,
              marketplaceFees: ['0x64300BB7090cC7e411845C85d5d5A4B911658f64:250']
              
            }}
            theme={reservoirKitTheme}
          >
            <CartProvider feesOnTopUsd={feesOnTop}>
            <WebsocketContextProvider>
              <Tooltip.Provider>
                <RainbowKitProvider
                  chains={chains}
                  theme={rainbowKitTheme}
                  modalSize="compact"
                >
                  <ToastContextProvider>
                    <FunctionalComponent {...pageProps} />
                    <Analytics />
                  </ToastContextProvider>
                </RainbowKitProvider>
              </Tooltip.Provider>
              </WebsocketContextProvider>
            </CartProvider>
          </ReservoirKitProvider>
        </ThemeProvider>
      </HotkeysProvider>
    )
  }
}

AppWrapper.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await NextApp.getInitialProps(appContext)
  let baseUrl = ''

  if (appContext.ctx.req?.headers.host) {
    const host = appContext.ctx.req?.headers.host
    baseUrl = `${host.includes('localhost') ? 'http' : 'https'}://${host}`
  } else if (process.env.NEXT_PUBLIC_HOST_URL) {
    baseUrl = process.env.NEXT_PUBLIC_HOST_URL || ''
  }
  baseUrl = baseUrl.replace(/\/$/, '')

  return { ...appProps, baseUrl }
}

export default AppWrapper